import { Routes } from '@angular/router';

import { MpsIsAdminGuard } from '../mps/guards/mps-isadmin.guard';
import { MpsLoggedInGuard } from '../mps/guards/mps-loggedin.guard';
import { applicationRouteNames } from './application-route-names';
import { appOnboardingGuard } from './guards/app-onboarding.guard';
import { appManageRoleGuard, appRoleGuard } from './guards/app-role.guard';
import { appWelcomeGuard } from './guards/app-welcome.guard';
import { homeRedirect } from './guards/home-redirect';
import { MpsRoleHelper } from './helpers/mps-role.helper';

// Staff = ADMIN_IT, ADMIN_METIER, COURRIER_AGENT — every matrix row that USER is hard-blocked
// from (spec 042).
const staffRoleGuard = () =>
  appRoleGuard(MpsRoleHelper.ADMIN_IT, MpsRoleHelper.ADMIN_METIER, MpsRoleHelper.COURRIER_AGENT);

// Management = ADMIN_IT, ADMIN_METIER only — the Performance dashboard is off-limits to
// COURRIER_AGENT and USER (spec 048, US5).
const managementRoleGuard = () => appRoleGuard(MpsRoleHelper.ADMIN_IT, MpsRoleHelper.ADMIN_METIER);

export const applicationRoutes: Routes = [
  {
    path: applicationRouteNames.welcome,
    loadComponent: () => import('./welcome/welcome.component').then((m) => m.WelcomeComponent),
    canActivate: [MpsLoggedInGuard, appWelcomeGuard],
  },
  {
    path: applicationRouteNames.search,
    loadComponent: () =>
      import('./courrier/search-results/search-results.component').then((m) => m.SearchResultsComponent),
    canActivate: [MpsLoggedInGuard, staffRoleGuard(), appOnboardingGuard],
  },
  {
    path: applicationRouteNames.agentDashboard,
    loadComponent: () => import('./agent-dashboard/agent-dashboard.component').then((m) => m.AgentDashboardComponent),
    // Not a global nav tab (no data.title): reachable via the staff-only button on the
    // courrier list header, next to "Importer des courriers".
    canActivate: [MpsLoggedInGuard, staffRoleGuard(), appOnboardingGuard],
    canLoad: [],
  },
  {
    path: applicationRouteNames.home,
    redirectTo: () => homeRedirect(),
  },
  {
    path: applicationRouteNames.courrier.index,
    loadComponent: () => import('./courrier/courrier.component').then((m) => m.CourrierComponent),
    data: { title: 'general.navigation.courrier' },
    canActivate: [MpsLoggedInGuard, staffRoleGuard(), appOnboardingGuard],
    canLoad: [MpsLoggedInGuard, staffRoleGuard()],
    children: [
      {
        path: '',
        loadComponent: () => import('./courrier/list/list.component').then((m) => m.ListComponent),
      },
      {
        path: applicationRouteNames.courrier.importReview,
        loadComponent: () =>
          import('./courrier/import-review/import-review.component').then((m) => m.ImportReviewComponent),
      },
    ],
  },
  {
    path: applicationRouteNames.administration,
    data: { title: 'general.navigation.administration' },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
    children: [
      {
        path: applicationRouteNames.recipientGroups.index,
        loadComponent: () =>
          import('./administration/recipient-groups/recipient-groups.component').then(
            (m) => m.RecipientGroupsComponent
          ),
        data: { title: 'general.navigation.recipient_groups' },
        canActivate: [staffRoleGuard()],
        canLoad: [staffRoleGuard()],
      },
      {
        path: `${applicationRouteNames.recipientGroups.index}/${applicationRouteNames.recipientGroups.create}`,
        loadComponent: () =>
          import('./administration/recipient-groups/create-recipient-group/create-recipient-group.component').then(
            (m) => m.CreateRecipientGroupComponent
          ),
        // Create is available to Admin IT, Admin Métier, and Courrier Agent (mirrors the backend write gate).
        canActivate: [appRoleGuard(MpsRoleHelper.ADMIN_IT, MpsRoleHelper.ADMIN_METIER, MpsRoleHelper.COURRIER_AGENT)],
        canLoad: [appRoleGuard(MpsRoleHelper.ADMIN_IT, MpsRoleHelper.ADMIN_METIER, MpsRoleHelper.COURRIER_AGENT)],
      },
      {
        path: applicationRouteNames.rolesPermissions.index,
        loadComponent: () =>
          import('./administration/roles-permissions/roles-permissions.component').then(
            (m) => m.RolesPermissionsComponent
          ),
        data: { title: 'general.navigation.roles_permissions' },
        canActivate: [staffRoleGuard()],
        canLoad: [staffRoleGuard()],
        children: [
          {
            path: '',
            redirectTo: applicationRouteNames.rolesPermissions.adminIt,
            pathMatch: 'full',
          },
          {
            path: applicationRouteNames.rolesPermissions.adminIt,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./administration/roles-permissions/role-detail/role-detail.component').then(
                    (m) => m.RoleDetailComponent
                  ),
                data: { roleCode: 'ADMIN_IT' },
              },
              {
                path: applicationRouteNames.rolesPermissions.edit,
                loadComponent: () =>
                  import('./administration/roles-permissions/role-edit/role-edit.component').then(
                    (m) => m.RoleEditComponent
                  ),
                canActivate: [appManageRoleGuard],
                data: { roleCode: 'ADMIN_IT' },
              },
            ],
          },
          {
            path: applicationRouteNames.rolesPermissions.adminMetier,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./administration/roles-permissions/role-detail/role-detail.component').then(
                    (m) => m.RoleDetailComponent
                  ),
                data: { roleCode: 'ADMIN_METIER' },
              },
              {
                path: applicationRouteNames.rolesPermissions.edit,
                loadComponent: () =>
                  import('./administration/roles-permissions/role-edit/role-edit.component').then(
                    (m) => m.RoleEditComponent
                  ),
                canActivate: [appManageRoleGuard],
                data: { roleCode: 'ADMIN_METIER' },
              },
            ],
          },
          {
            path: applicationRouteNames.rolesPermissions.courrier,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./administration/roles-permissions/role-detail/role-detail.component').then(
                    (m) => m.RoleDetailComponent
                  ),
                data: { roleCode: 'COURRIER_AGENT' },
              },
              {
                path: applicationRouteNames.rolesPermissions.edit,
                loadComponent: () =>
                  import('./administration/roles-permissions/role-edit/role-edit.component').then(
                    (m) => m.RoleEditComponent
                  ),
                canActivate: [appManageRoleGuard],
                data: { roleCode: 'COURRIER_AGENT' },
              },
            ],
          },
        ],
      },
      {
        path: applicationRouteNames.users,
        loadComponent: () => import('./administration/users/list/list.component').then((m) => m.ListComponent),
        data: { title: 'general.navigation.users' },
        canActivate: [MpsIsAdminGuard],
        canLoad: [MpsIsAdminGuard],
      },
      {
        path: applicationRouteNames.lokalise,
        loadComponent: () => import('./administration/lokalise/lokalise.component').then((m) => m.LokaliseComponent),
        data: { title: 'general.navigation.lokalise' },
        canActivate: [MpsIsAdminGuard],
        canLoad: [MpsIsAdminGuard],
      },
      {
        path: applicationRouteNames.allowedCities,
        loadComponent: () => import('./administration/allowed-cities/list/list.component').then((m) => m.ListComponent),
        data: { title: 'general.navigation.allowed_cities' },
        canActivate: [MpsIsAdminGuard],
        canLoad: [MpsIsAdminGuard],
      },
    ],
  },
  {
    path: applicationRouteNames.performance,
    loadComponent: () => import('./performance/performance.component').then((m) => m.PerformanceComponent),
    data: { title: 'general.navigation.performance' },
    canActivate: [MpsLoggedInGuard, managementRoleGuard(), appOnboardingGuard],
    canLoad: [managementRoleGuard()],
  },
  {
    path: applicationRouteNames.monCourrier,
    loadComponent: () => import('./mon-courrier/list/list.component').then((m) => m.MonCourrierListComponent),
    data: { title: 'general.navigation.mon_courrier' },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
  },
  {
    path: `${applicationRouteNames.monCourrier}/deliveries/:deliveryId`,
    loadComponent: () =>
      import('./mon-courrier/courrier-viewer/courrier-viewer.component').then((m) => m.CourrierViewerComponent),
    data: { backRoute: applicationRouteNames.monCourrier },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
  },
  {
    path: applicationRouteNames.courrierDelegues,
    loadComponent: () =>
      import('./mon-courrier/courrier-delegues/courrier-delegues.component').then((m) => m.CourrierDeleguesComponent),
    data: { title: 'general.navigation.courrier_delegues' },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
  },
  {
    path: `${applicationRouteNames.courrierDelegues}/deliveries/:deliveryId`,
    loadComponent: () =>
      import('./mon-courrier/courrier-viewer/courrier-viewer.component').then((m) => m.CourrierViewerComponent),
    data: { backRoute: applicationRouteNames.courrierDelegues },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
  },
  {
    path: applicationRouteNames.parametresAide,
    loadComponent: () =>
      import('./mon-courrier/parametres-aide/parametres-aide.component').then((m) => m.ParametresAideComponent),
    data: { title: 'general.navigation.parametres_aide' },
    canActivate: [MpsLoggedInGuard, appOnboardingGuard],
    canLoad: [MpsLoggedInGuard],
  },
];
